// src/lib/api.ts
// Data-layer adapter. The Readdy pages were built against Readdy's Supabase; this
// repo serves them from the KWD template's Cloudflare Pages Functions (D1 + Square)
// instead. These helpers do same-origin fetches against /api/* and the hooks
// (useProducts / useCategories) map the responses into the SAME TypeScript shapes
// the pages already consume — so no page/component/styling changes are needed.

// ---- Raw template API response shapes ----
export interface ApiListItem {
  id: string;
  name: string;
  description: string | null;
  category: string;
  price: number;
  stock: number;
  image: string;
  gallery: string[];
  hasVariants: boolean;
  variantCount: number;
  currency: string;
}

export interface ApiVariant {
  id: string;
  name: string;
  optionLabel: string;
  options: { name: string; value: string }[];
  price: number;
  stock: number;
  image: string;
  url: string;
}

export interface ApiProductDetail {
  id: string;
  name: string;
  price: number | null;
  description: string | null;
  image: string;
  category: string;
  gallery: string[];
  parentId: string | null;
  optionLabel: string;
  currency: string;
  variants: ApiVariant[];
  hasVariants: boolean;
  error?: string;
}

export interface AppConfig {
  businessName: string;
  currency: string;
  paymentProvider: string;
  squareEnabled: boolean;
  squareEnvironment: string;
  squareApplicationId: string;
  squareLocationId: string;
}

// hashId — stable non-negative int from a string. Used to give each template
// category (a string name) a numeric id, because the Readdy pages model categories
// with numeric ids. Deterministic so the same name always maps to the same id
// across useCategories (the filter list) and useProducts (a product's category_id).
export function hashId(s: string): number {
  const str = String(s || "");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

export interface MappedCategory {
  id: number;
  name: string;
  count: number;
  sort_order: number;
}

// loadCategories — fetch + cache /api/categories once (a single in-flight promise),
// mapping each { name, count } to { id: hashId(name), name, count, sort_order }.
let _cats: Promise<MappedCategory[]> | null = null;
export function loadCategories(): Promise<MappedCategory[]> {
  if (!_cats) {
    _cats = fetch("/api/categories")
      .then((r) => (r.ok ? r.json() : { categories: [] }))
      .then((d) =>
        ((d && d.categories) || []).map((c: { name: string; count: number }, i: number) => ({
          id: hashId(c.name),
          name: c.name,
          count: c.count,
          sort_order: i,
        }))
      )
      .catch(() => []);
  }
  return _cats;
}

// categoryNameFromId — reverse the numeric category id back to its template name so
// the products list can pass ?category=<name>. Resolves against the cached list.
export async function categoryNameFromId(id: number): Promise<string | undefined> {
  const cats = await loadCategories();
  return cats.find((c) => c.id === id)?.name;
}

// apiListProducts — fetch ALL pages of /api/products for a category/search (the
// template caps limit at 60; the storefront grids show everything, so we page).
export async function apiListProducts(categoryName?: string, q?: string): Promise<ApiListItem[]> {
  const out: ApiListItem[] = [];
  const limit = 60;
  let offset = 0;
  for (let guard = 0; guard < 50; guard++) {
    const params = new URLSearchParams();
    if (categoryName) params.set("category", categoryName);
    if (q) params.set("q", q);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    const d = await fetch(`/api/products?${params.toString()}`).then((r) => (r.ok ? r.json() : { items: [], total: 0 }));
    const items: ApiListItem[] = (d && d.items) || [];
    out.push(...items);
    const total = (d && typeof d.total === "number") ? d.total : out.length;
    offset += items.length;
    if (items.length === 0 || out.length >= total) break;
  }
  return out;
}

// apiGetProduct — fetch a single product detail (+ variants). null on 404.
export async function apiGetProduct(id: string): Promise<ApiProductDetail | null> {
  const r = await fetch(`/api/products/${encodeURIComponent(id)}`);
  if (!r.ok) return null;
  return r.json();
}

// loadConfig — fetch + cache /api/config (business name, currency, Square keys).
let _cfg: Promise<AppConfig | null> | null = null;
export function loadConfig(): Promise<AppConfig | null> {
  if (!_cfg) {
    _cfg = fetch("/api/config").then((r) => (r.ok ? r.json() : null)).catch(() => null);
  }
  return _cfg;
}

// postQuote — server-side subtotal + shipping + total for a cart (shipping is
// computed from each product's shipping_class; never trust the browser).
export async function postQuote(items: { id: string; qty: number }[]): Promise<{ subtotal: number; shipping: number; total: number; currency: string } | null> {
  try {
    const r = await fetch("/api/checkout/quote", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!r.ok) return null;
    return r.json();
  } catch {
    return null;
  }
}
